import { VehicleRoute } from "../../../../redux/actions/routeActionTypes";

export type FleetSnapshot = {
  savedAt: number;
  routes: VehicleRoute[];
};

const KEY = "sp.map.fleet.capmetro";

// The bare cache this replaces. Cleared on the way past so it stops taking up
// room in everyone's localStorage.
const LEGACY_KEY = "routes_cache";

// Past this, the snapshot stops being "what you were looking at a moment ago"
// and starts being a claim about the fleet that nobody checked. Better an empty
// map for a few seconds than a confident, hours-old one.
export const SNAPSHOT_MAX_AGE_MS = 60 * 60 * 1000;

// localStorage is a handful of megabytes for the whole origin; don't spend it
// all on one map.
const MAX_BYTES = 2000000;

// Every call is wrapped: storage throws outright in private windows and when
// the quota is full, and a half-written entry parses to junk. A snapshot is a
// convenience -- it must never be able to take the map down with it.
export function saveFleetSnapshot(
  routes: VehicleRoute[],
  savedAt: number
): void {
  if (!Array.isArray(routes) || routes.length === 0) return;
  try {
    const body = JSON.stringify({ savedAt, routes } as FleetSnapshot);
    if (body.length > MAX_BYTES) return;
    window.localStorage.setItem(KEY, body);
  } catch {
    /* no snapshot this time; the map still works */
  }
}

export function loadFleetSnapshot(now: number): FleetSnapshot | null {
  try {
    window.localStorage.removeItem(LEGACY_KEY);

    const body = window.localStorage.getItem(KEY);
    if (!body) return null;

    const parsed = JSON.parse(body);
    if (
      !parsed ||
      typeof parsed.savedAt !== "number" ||
      !Array.isArray(parsed.routes) ||
      parsed.routes.length === 0
    ) {
      return null;
    }
    if (now - parsed.savedAt > SNAPSHOT_MAX_AGE_MS || parsed.savedAt > now) {
      return null;
    }
    return parsed as FleetSnapshot;
  } catch {
    return null;
  }
}
